import { Mail, Phone, MapPin, MessageCircle, Clock } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import PageHero from "@/components/PageHero";
import QuoteForm from "@/components/QuoteForm";
import { Reveal } from "@/components/Reveal";
import { BRAND, IMAGES } from "@/lib/site";
import { getProductBySlug, getCategoryBySlug } from "@/lib/catalog";

export default function Contact() {
  const whatsappUrl = `https://wa.me/${BRAND.phoneRaw}?text=${encodeURIComponent(BRAND.whatsappMsg)}`;
  const [params] = useSearchParams();
  const productSlug = params.get("product");
  const detectedProduct = productSlug ? getProductBySlug(productSlug) : null;
  const detectedCategory = detectedProduct ? getCategoryBySlug(detectedProduct.category) : null;
  const defaultProductInterest = detectedProduct
    ? `${detectedProduct.name} — ${detectedCategory?.name || ""}`.replace(/\s+—\s+$/, "")
    : "";
  const defaultMessage = detectedProduct
    ? `Inquiry about ${detectedProduct.name} (${detectedCategory?.name || "Stenzia Displays"}).\n\nPlease share specifications, pricing and lead time.\n\n`
    : "";
  return (
    <div data-testid="contact-page">
      <PageHero
        overline="Contact"
        title="Let's engineer your next showroom."
        subtitle="Share your requirements and our team will respond within 24 hours with concept drawings and indicative pricing."
        image={IMAGES.contactus}
      />

      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <div className="overline text-stenzia-green"><span className="kbd-line" /> Reach us</div>
            <h2 className="mt-6 font-display font-light text-3xl md:text-4xl tracking-tighter text-charcoal leading-[1.05]">Talk to our engineers.</h2>

            <div className="mt-10 space-y-5">
              <a href={`mailto:${BRAND.email}`} data-testid="contact-email" className="flex items-start gap-4 group p-4 rounded-2xl hover:bg-[#F6F7F5] transition-all">
                <div className="w-12 h-12 rounded-xl bg-stenzia-green/10 text-stenzia-green flex items-center justify-center group-hover:bg-stenzia-green/20 group-hover:text-stenzia-green group-hover:scale-110 transition-all duration-500"><Mail size={20} /></div>
                <div>
                  <div className="overline text-charcoal/40">Email</div>
                  <div className="mt-1 font-display text-lg text-charcoal group-hover:text-stenzia-green transition-colors">{BRAND.email}</div>
                </div>
              </a>
              <a href={`tel:${BRAND.phone.replace(/\s+/g, "")}`} data-testid="contact-phone" className="flex items-start gap-4 group p-4 rounded-2xl hover:bg-[#F6F7F5] transition-all">
                <div className="w-12 h-12 rounded-xl bg-stenzia-green/10 text-stenzia-green flex items-center justify-center group-hover:bg-stenzia-green/20 group-hover:text-stenzia-green group-hover:scale-110 transition-all duration-500"><Phone size={20} /></div>
                <div>
                  <div className="overline text-charcoal/40">Sales </div>
                  <div className="mt-1 font-display text-lg text-charcoal group-hover:text-stenzia-green transition-colors">{BRAND.phone}</div>
                </div>
              </a>
              <a href={`tel:${BRAND.phoneSecondary.replace(/\s+/g, "")}`} data-testid="contact-phone-secondary" className="flex items-start gap-4 group p-4 rounded-2xl hover:bg-[#F6F7F5] transition-all">
                <div className="w-12 h-12 rounded-xl bg-stenzia-green/10 text-stenzia-green flex items-center justify-center group-hover:bg-stenzia-green/20 group-hover:text-stenzia-green group-hover:scale-110 transition-all duration-500"><Phone size={20} /></div>
                <div>
                  <div className="overline text-charcoal/40">Technical Support</div>
                  <div className="mt-1 font-display text-lg text-charcoal group-hover:text-stenzia-green transition-colors">{BRAND.phoneSecondary}</div>
                </div>
              </a>
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="flex items-start gap-4 group p-4 rounded-2xl hover:bg-[#25D366]/5 transition-all">
                <div className="w-12 h-12 rounded-xl bg-[#25D366]/10 text-[#1B8C4A] flex items-center justify-center group-hover:bg-[#25D366] group-hover:text-white group-hover:scale-110 transition-all duration-500"><MessageCircle size={20} /></div>
                <div>
                  <div className="overline text-charcoal/40">WhatsApp</div>
                  <div className="mt-1 font-display text-lg text-charcoal group-hover:text-[#1B8C4A] transition-colors">Chat with us instantly</div>
                </div>
              </a>
              <div data-testid="contact-address" className="flex items-start gap-4 p-4 rounded-2xl">
                <div className="w-12 h-12 rounded-xl bg-stenzia-green/10 text-stenzia-green flex items-center justify-center group-hover:bg-stenzia-green/20 group-hover:text-stenzia-green group-hover:scale-110 transition-all duration-500"><MapPin size={20} /></div>
                <div>
                  <div className="overline text-charcoal/40">Address</div>
                  <div className="mt-1 font-display text-lg text-charcoal">{BRAND.address}</div>
                </div>
              </div>
            </div>

            <div className="mt-8 p-6 rounded-2xl bg-charcoal text-white relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-stenzia-gold/10 blur-2xl" />
              <div className="relative flex items-center gap-3">
                <Clock size={18} className="text-stenzia-gold" />
                <div className="overline text-stenzia-gold">Office Hours</div>
              </div>
              <p className="mt-3 text-sm text-white/75 leading-relaxed">Monday – Saturday · 9:30 AM – 7:00 PM (IST)<br />Closed on Sundays and Indian public holidays.</p>
            </div>
          </Reveal>

          <Reveal delay={1} className="lg:col-span-7">
            <div className="bg-[#F6F7F5] rounded-3xl p-6 md:p-10 border border-charcoal/10 shadow-sm">
              <h3 className="font-display text-2xl md:text-3xl text-charcoal tracking-tight">Request a Quote</h3>
              <p className="mt-2 text-sm text-charcoal/60">All fields marked with * are required.</p>
              <div className="mt-8">
                <QuoteForm
                  defaultProductInterest={defaultProductInterest}
                  defaultMessage={defaultMessage}
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Map */}
      <section className="relative">
        <Reveal className="max-w-7xl mx-auto px-6 md:px-12 pb-20">
          <div className="overline text-stenzia-green mb-6"><span className="kbd-line" /> Find Us</div>
          <div className="relative w-full h-[420px] md:h-[520px] rounded-3xl overflow-hidden shadow-2xl border border-charcoal/10">
            <iframe
              title="Stenzia Displays — Morbi, Gujarat"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3538.416748386637!2d70.86077637504698!3d22.829421423509068!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39598d5b9769539d%3A0xea2f556d3849955d!2sSTENZIA%20DISPLAYS%20LLP!5e1!3m2!1sen!2sin!4v1784470802207!5m2!1sen!2sin"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="contact-map"
            />
              <div className="absolute top-6 right-6 max-w-xs p-5 rounded-2xl bg-white/95 backdrop-blur-xl border border-white shadow-xl z-20">
              <div className="overline text-stenzia-green">Stenzia Displays LLP</div>
              <div className="mt-2 font-display text-lg text-charcoal">Morbi, Gujarat — India</div>
              <div className="mt-2 text-xs text-charcoal/60">World&apos;s second-largest ceramic tile hub.</div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
